/**
 * tutorProfileAdapter — le compte connecté et les données statiques → « Compléter mon profil », et le
 * retour : le brouillon et la semaine → le corps de `tutorsService.submitCompleteProfile`.
 *
 * Port de `tutor/home/CompleteTutorProfileStepper.js` (les `defaultValue`, les `validationRules`,
 * `onSubmit`, `isAvailabilitySlotsValid`), de `ForeignLanguageStep.js` / `LanguageSelector.js` (les
 * trois niveaux, 1 à 3) et de `TutorProfileCardHeader` (`StartTrialNewScreen.js` : la bio « filière |
 * slogan », « (mon slogan) », les matières en chips). Les listes viennent de `formConstants`, les
 * mêmes que l'ancien parcours.
 *
 * Le serveur (`TutorController::updateProfile`) écrit ce qu'on lui envoie, `null` compris : un
 * `num_SIRET` à `null` efface le SIRET (celui que Stripe Connect lit). Il garde les créneaux engagés
 * quoi qu'on poste : seuls les créneaux proposés partent. L'IBAN ne part plus (03/11) : le
 * versement passe par le compte Stripe du tuteur, et le profil se complète sans lui.
 */
import {
    sectionStudentClasses,
    skillSetIds,
    studentClasses,
    subjectPriorities,
    subjectPrioritiesSubjectsDisplay,
} from "../../../../../data/constants/formConstants"
import { REQUIRED_MESSAGE } from "../../student/Account/studentSettingsAdapter"
import { slotDayOf, WEEKDAY_ORDER } from "../shared/tutorWeekAdapter"
import { subjectColorToken } from "../../parent/shared/schoolSubjectAdapter"
import { type UISubject } from "../../../ui/types"
import { type UITutorLanguageLevel } from "../../../ui/types/tutorCandidature"
import {
    type UISelectOptionSection,
    type UITutorProfileCatalogs,
    type UITutorProfileDraft,
    type UITutorProfilePreview,
} from "../../../ui/types/tutorCompte"

// ── Formes lâches du serveur ────────────────────────────────────────────────

export type ApiTutorProfileUser = {
    id?: number | null
    display_name?: string | null
    avatar?: string | null
    title_skill_set_id?: number | null
    specialty_school_subjects?: { id?: number | string | null }[] | null
    foreign_languages?: { id?: number | string | null; name?: string | null; proficiency?: number | null }[] | null
    profile?: {
        fixed_missions?: boolean | number | null
        replace_missions?: boolean | number | null
        relevant_subjects?: string[] | null
        relevant_classes?: string[] | null
        admin_approved_classes?: string[] | null
        num_SIRET?: string | null
        study_field?: string | null
        study_institution?: string | null
    } | null
}

/** Une matière des données statiques (`static-data.school_subjects`). */
export type ApiSchoolSubject = {
    id?: number | string | null
    name?: string | null
    is_specialty?: boolean | number | null
    category?: string | null
    slug?: string | null
    color?: string | null
    icon?: string | null
}

// ── Les constantes de l'ancien parcours ─────────────────────────────────────

/** Le slogan d'un tuteur qui n'en a pas choisi : le premier de la liste. */
const DEFAULT_SLOGAN_ID = 101

/** `LanguageSelector.js` : Conversationnel 1, Courant 2, Langue maternelle 3. */
const LEVEL_BY_PROFICIENCY: Record<number, UITutorLanguageLevel> = { 1: "conversational", 2: "fluent", 3: "native" }
const PROFICIENCY_BY_LEVEL: Record<UITutorLanguageLevel, number> = { conversational: 1, fluent: 2, native: 3 }

// La section « Autre » (dernière) n'est pas proposée, comme dans l'ancienne étape.
const PROFILE_CLASS_SECTIONS = sectionStudentClasses.slice(0, -1)
const PROFILE_CLASSES = studentClasses.slice(0, -1)

export const LANGUAGE_LEVEL_MISSING = "Veuillez sélectionner le niveau de maîtrise pour chaque langue"
export const CHARTER_REQUIRED = "Tu dois accepter la charte d'engagement du tuteur"
export const AVAILABILITY_REQUIRED = "Ajoute au moins un créneau"

const toBooleanOrNull = (value: boolean | number | null | undefined) => (value == null ? null : !!value)

/** L'équipe a approuvé des classes : la question le dit, et seules ces classes s'offrent. */
export const approvedClassesOf = (user: ApiTutorProfileUser | null | undefined): string[] =>
    user?.profile?.admin_approved_classes ?? []

/** Les réponses déjà données, ou ce que l'ancien parcours pré-remplissait. La charte se ré-accepte. */
export function toTutorProfileDraft(user: ApiTutorProfileUser): UITutorProfileDraft {
    const profile = user.profile ?? {}
    const approved = approvedClassesOf(user)
    return {
        fixedMissions: toBooleanOrNull(profile.fixed_missions),
        replaceMissions: toBooleanOrNull(profile.replace_missions),
        specialtySubjectIds: (user.specialty_school_subjects ?? [])
            .filter((subject) => subject?.id != null)
            .map((subject) => String(subject.id)),
        languages: (user.foreign_languages ?? [])
            .filter((language) => language?.id != null)
            .map((language) => ({
                id: String(language.id),
                name: language.name ?? "",
                level: LEVEL_BY_PROFICIENCY[language.proficiency ?? 0] ?? null,
            })),
        relevantSubjectIds: profile.relevant_subjects ?? [],
        // Un nouveau tuteur a toutes les classes cochées, sauf quand l'équipe en a approuvé.
        relevantClassIds: profile.relevant_classes ?? (approved.length > 0 ? [] : PROFILE_CLASSES),
        // `num_SIRET !== null` : une clé absente compte comme un statut déjà pris, comme l'ancien.
        isAutoEntrepreneur: profile.num_SIRET !== null,
        siret: profile.num_SIRET ?? "",
        studyInstitution: profile.study_institution ?? "",
        studyField: profile.study_field ?? "",
        sloganId: String(user.title_skill_set_id ?? DEFAULT_SLOGAN_ID),
        charterAccepted: false,
    }
}

/** Une matière du catalogue, avec son picto et sa couleur ; l'id en texte, comme le brouillon. */
const subjectChoice = (subject: ApiSchoolSubject): UISubject => ({
    id: String(subject.id),
    name: subject.name ?? "",
    color: subjectColorToken({ slug: subject.slug ?? undefined, color: subject.color ?? undefined }),
    iconUrl: subject.icon ?? undefined,
})

/** Les listes d'options des étapes ; les classes bornées à celles approuvées quand il y en a. */
export function toTutorProfileCatalogs(
    subjects: ApiSchoolSubject[] | null | undefined,
    approvedClasses: string[]
): UITutorProfileCatalogs {
    const known = (subjects ?? []).filter((subject) => subject?.id != null)
    const classSections: UISelectOptionSection[] = PROFILE_CLASS_SECTIONS.map((section) => ({
        id: section.title,
        title: section.title,
        options: section.options
            .filter((option) => approvedClasses.length === 0 || approvedClasses.includes(option))
            .map((option) => ({ id: option, label: option })),
    })).filter((section) => section.options.length > 0)
    return {
        specialtySubjects: known.filter((subject) => !!subject.is_specialty).map(subjectChoice),
        languages: known
            .filter((subject) => subject.category === "language")
            .map((subject) => {
                const { id, name, color, iconUrl } = subjectChoice(subject)
                return { id: String(id), name, color, iconUrl }
            }),
        relevantSubjects: subjectPriorities.map((option) => ({ id: option.value, label: option.label })),
        classSections,
        slogans: skillSetIds.map((option) => ({ id: String(option.value), label: option.label })),
    }
}

/** « Tu apparaîtras de cette façon » — l'aperçu suit le brouillon, pas le compte. */
export function toTutorProfilePreview(user: ApiTutorProfileUser, draft: UITutorProfileDraft): UITutorProfilePreview {
    const slogan = skillSetIds.find((option) => String(option.value) === draft.sloganId)?.label
    const chips = (subjectPrioritiesSubjectsDisplay as Record<string, string[]>)[draft.relevantSubjectIds.join(",")]
    return {
        displayName: user.display_name ?? "",
        avatarUrl: user.avatar ?? null,
        studyInstitution: draft.studyInstitution,
        bio: `${draft.studyField} | ${slogan ?? "(mon slogan)"}`,
        chips: chips ?? [],
    }
}

// ── Les règles des étapes ───────────────────────────────────────────────────

/** Les étapes qui se valident sur le brouillon ; les créneaux se valident sur la semaine. */
export type TutorProfileDraftStep =
    | "fixedMissions"
    | "replaceMissions"
    | "specialties"
    | "languages"
    | "relevantSubjects"
    | "classes"
    | "billing"
    | "personalize"
    | "charter"

export type TutorProfileErrors = Partial<Record<keyof UITutorProfileDraft, string>>

const blank = (value: string) => value.trim() === ""

/** Les `validationRules` de l'ancien parcours, étape par étape. Vide : l'étape passe. */
export function validateTutorProfileStep(step: TutorProfileDraftStep, draft: UITutorProfileDraft): TutorProfileErrors {
    switch (step) {
        case "fixedMissions":
        case "replaceMissions":
            return draft[step] == null ? { [step]: REQUIRED_MESSAGE } : {}
        case "specialties":
            return draft.specialtySubjectIds.length === 0 ? { specialtySubjectIds: REQUIRED_MESSAGE } : {}
        case "languages":
            // Facultative : seule une langue sans niveau la retient.
            return draft.languages.some((language) => language.level == null)
                ? { languages: LANGUAGE_LEVEL_MISSING }
                : {}
        case "relevantSubjects":
            return draft.relevantSubjectIds.length === 0 ? { relevantSubjectIds: REQUIRED_MESSAGE } : {}
        case "classes":
            return draft.relevantClassIds.length === 0 ? { relevantClassIds: REQUIRED_MESSAGE } : {}
        case "billing":
            // Le SIRET n'est pas exigé (l'ancien parcours non plus) ; l'IBAN se donne à Stripe.
            return {}
        case "personalize": {
            const errors: TutorProfileErrors = {}
            if (blank(draft.studyInstitution)) errors.studyInstitution = REQUIRED_MESSAGE
            if (blank(draft.studyField)) errors.studyField = REQUIRED_MESSAGE
            if (draft.sloganId === "") errors.sloganId = REQUIRED_MESSAGE
            return errors
        }
        case "charter":
            return draft.charterAccepted ? {} : { charterAccepted: CHARTER_REQUIRED }
    }
}

/**
 * Le conseil « Indique un maximum de créneaux… » (`isAvailabilitySlotsValid`) : moins de trois
 * créneaux, sauf deux qui ne tombent pas deux jours qui se suivent. Compte la semaine ENTIÈRE, les
 * créneaux engagés compris.
 */
export function availabilityNeedsAdvice(slots: string[]): boolean {
    if (slots.length >= 3) return false
    if (slots.length !== 2) return true
    const [first, second] = slots.map((slot) => WEEKDAY_ORDER.indexOf(slotDayOf(slot)))
    return first >= 0 && second >= 0 && Math.abs(first - second) === 1
}

// ── L'envoi ─────────────────────────────────────────────────────────────────

/** Le corps de `submitCompleteProfile` : les clés de l'ancien `onSubmit`. */
export function toCompleteProfilePayload(
    draft: UITutorProfileDraft,
    availability: { openSlots: string[]; startingDate: string | null }
): Record<string, unknown> {
    return {
        fixed_missions: draft.fixedMissions,
        replace_missions: draft.replaceMissions,
        specialty_school_subjects: draft.specialtySubjectIds.map(Number),
        foreign_languages: draft.languages.map((language) => ({
            id: Number(language.id),
            proficiency: language.level == null ? null : PROFICIENCY_BY_LEVEL[language.level],
        })),
        relevant_subjects: draft.relevantSubjectIds,
        relevant_classes: draft.relevantClassIds,
        // Le champ part même caché, comme l'ancien : décocher le statut ne doit pas effacer le SIRET du
        // compte, que le serveur écrirait à `null`.
        num_SIRET: blank(draft.siret) ? null : draft.siret,
        study_field: draft.studyField,
        study_institution: draft.studyInstitution,
        tutor_skill_set_title_id: Number(draft.sloganId),
        availability_slots: availability.openSlots,
        availability_starting_date: availability.startingDate,
        charter_opt_in: draft.charterAccepted,
    }
}
